import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { WaveBand } from './_waveBand';

// Google Maps embed key (optional — falls back to keyless maps.google embed).
const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const callLabel = (useContentToken('contactform.call_label') as string) || 'Call us at';
  const phone = (useContentToken('contactform.phone') as string) || '';
  const email = (useContentToken('contactform.email') as string) || '';
  const mapLabel = (useContentToken('contactform.map_label') as string) || 'Find us on the map';

  // Map source — mirrors ContactMap: prefer lat/lng, then full address.
  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;
  const hasCoords = !!(lat && lng && lat.trim() !== '' && lng.trim() !== '');

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=11`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=11&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&z=11&output=embed`;
  }

  return (
    <section className="cf-section">
      <div className="cf-grid">
        {/* ── Left column: heading, intro, contact lines, and lead form ── */}
        <div className="cf-content">
          <RichTextContent
            path="contactform.heading"
            label="Heading"
            defaultValue="<p>Reach out to us</p>"
            as="h1"
            className="cf-heading"
          />

          <RichTextContent
            path="contactform.intro"
            label="Intro"
            defaultValue='<p>Ready to make <span data-type="field-token" data-field="property_name"></span> your new home? Apply now and take the first step toward enjoying spacious, modern apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>'
            as="div"
            className="cf-intro"
          />

          <div className="cf-lines">
            <p className="cf-line">
              <EditableToken path="contactform.call_label" as="span">
                {callLabel}
              </EditableToken>{' '}
              {phone ? (
                <a href={`tel:${phone}`} className="cf-link dniphone dniphonehref">
                  <EditableToken path="contactform.phone" as="span">
                    {phone}
                  </EditableToken>
                </a>
              ) : (
                <EditableToken path="contactform.phone" as="span">
                  {phone}
                </EditableToken>
              )}
            </p>

            {email ? (
              <a href={`mailto:${email}`} className="cf-line cf-link">
                <EditableToken path="contactform.email" as="span">
                  {email}
                </EditableToken>
              </a>
            ) : (
              <EditableToken path="contactform.email" as="p" className="cf-line">
                {email}
              </EditableToken>
            )}
          </div>

          <div className="cf-form-wrap">
            <EditableToken path="contactform.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="cf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>
        </div>

        {/* ── Right column: full-height map ── */}
        <div className="cf-map">
          <EditableToken path="contactform.map_label" as="h2" className="cf-sr-only">
            {mapLabel}
          </EditableToken>
          {mapSrc ? (
            <iframe
              className="cf-map-iframe"
              src={mapSrc}
              title={mapLabel}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="cf-map-placeholder">
              <p>Map location not available</p>
            </div>
          )}
        </div>
      </div>

      {/* Decorative wave band closing out the stripe — same geometry as the
          hero / overview bands, sitting flush above the footer. */}
      {/* On mobile, match the wave band under the interior heros (HeroImageBG:
          height 70 → 35 / spacing 15 → 7.5 / amp 25 → 12.5 halved). Desktop
          keeps the taller closing-band geometry. */}
      <WaveBand
        height={240}
        spacing={15}
        amp={25}
        mobileHeight={35}
        mobileSpacing={7.5}
        mobileAmp={12.5}
        className="w-full bg-[var(--vibe-color-background)]"
      />

      <style>{`
        .cf-section {
          width: 100%;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          /* Push the whole two-column band (map + copy) down together so
             both have breathing room below the nav. */
          .cf-section { padding-top: 6.25rem; }
        }
        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
        }
        @media (min-width: 768px) {
          .cf-grid {
            /* Explicit, equal 50/50 split (minmax(0,1fr) keeps the columns
               even when the form block has a wide intrinsic width). */
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            align-items: stretch;
          }
        }

        /* ── Left content column ──
           Extra top padding on mobile pushes the h1 clear of the fixed nav so
           it isn't hidden behind the header on small screens. */
        .cf-content {
          padding: 6rem 1.5rem 3rem;
        }
        @media (min-width: 768px) {
          /* Symmetric, moderate padding so the form area fills its half and
             reads the same width as the map. Extra top padding gives the
             heading breathing room below the nav. */
          .cf-content {
            padding: 7rem 2.5rem 5rem clamp(1.5rem, 3vw, 3rem);
          }
        }

        .cf-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2.25rem, 5vw, 3rem);
          font-weight: 600;
          line-height: 1.05;
          color: var(--vibe-color-primary);
          margin: 0 0 1.5rem;
        }
        .cf-heading p { margin: 0; }

        .cf-intro {
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          max-width: 34rem;
          margin: 0 0 1.5rem;
        }
        .cf-intro p { margin: 0; }

        .cf-lines {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin-bottom: 2rem;
        }
        .cf-line {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-link {
          color: var(--vibe-color-text);
          text-decoration: none;
          transition: color 0.2s;
        }
        .cf-link:hover { color: var(--vibe-color-primary); }

        .cf-form-wrap {
          max-width: 40rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        /* ── Right map column ── */
        .cf-map {
          position: relative;
          min-height: 340px;
        }
        @media (min-width: 768px) {
          /* Match the copy column's top padding so the map's top edge lines up
             with the heading, leaving empty space below the header. */
          .cf-map { min-height: 100%; padding-top: 7rem; }
        }
        .cf-map-iframe,
        .cf-map-placeholder {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 340px;
          border: none;
        }
        .cf-map-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-surface, #f5f5f5);
        }
        .cf-map-placeholder p {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .cf-sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        /* ── Multihub form card exterior — square corners ──
           The lead-form block renders its outer card (.mh-lead-form-card) with
           a rounded radius set inline; override to sharp corners so it matches
           the squared cards used elsewhere on the site. Inline styles are not
           !important, so a stylesheet !important wins. */
        .cf-form-wrap .mh-lead-form-card {
          border-radius: 0 !important;
        }

        /* ── CTA submit button — match the site's filled pill ──
           The lead-form block renders its submit button with an 8px radius set
           inline; override to a fully-rounded pill with the same hover lift as
           the header's .nav-cta-btn so it reads as a site CTA. Not tablet-only. */
        .cf-form-wrap .mh-lead-form-submit-button {
          border-radius: 9999px !important;
          transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: color-mix(in srgb, var(--vibe-color-primary), black 12%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: none;
        }

        /* ── Tablet form sizing ──
           On tablet the form sits in a ~half-width column, so its two-up rows
           (First/Last name, Move-in date/Bedrooms) get cramped and placeholder
           text truncates. Shrink the field font-size here so the tip text and
           entered text fit. The form is the read-only lead-form block, which
           sets font-size inline, so these overrides need !important. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .cf-form-wrap .mh-lead-form-card input,
          .cf-form-wrap .mh-lead-form-card select,
          .cf-form-wrap .mh-lead-form-card textarea,
          .cf-form-wrap .mh-lead-form-card .mh-lead-form-date-field span {
            font-size: 0.8125rem !important;
          }
        }
        /* ── Mobile form sizing ──
           On phones the form is full-width but the viewport is narrow, so the
           input/placeholder (tip) text still crowds. Shrink it a bit more than
           the tablet override so entered text and tip text fit comfortably. */
        @media (max-width: 767px) {
          .cf-form-wrap .mh-lead-form-card input,
          .cf-form-wrap .mh-lead-form-card select,
          .cf-form-wrap .mh-lead-form-card textarea,
          .cf-form-wrap .mh-lead-form-card .mh-lead-form-date-field span {
            font-size: 0.75rem !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Reach out to us</p>',
    },
    'contactform.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>Ready to make <span data-type="field-token" data-field="property_name"></span> your new home? Apply now and take the first step toward enjoying spacious, modern apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>',
    },
    'contactform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call us at',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.email': {
      label: 'Email',
      type: 'email',
      defaultField: 'property_email',
      defaultValue: '',
    },
    'contactform.map_label': {
      label: 'Map Label (screen-reader)',
      type: 'text',
      defaultValue: 'Find us on the map',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
